import { Suspense } from "react";
import { lazyWithRetry } from "@/utils/lazyWithRetry";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PaymentGuard } from "@/components/payments/PaymentGuard";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminGuard } from "@/components/AdminGuard";
import { OnboardingGuard } from "@/components/OnboardingGuard";
import { MainLayout } from "@/layouts/MainLayout";
import { AuthLayout } from "@/layouts/AuthLayout";
import LoginPage from "@/pages/authentication/login/LoginPage";
import LoginEmailCheckPage from "@/pages/authentication/login/LoginEmailCheckPage";
import UnauthorizedPage from "@/pages/UnauthorizedPage";
import NotFoundPage from "@/pages/NotFoundPage";
import { SignUpPage } from "@/pages/authentication/signup/SignUpPage";
import { VerifyEmailPage } from "@/pages/verify/VerifyEmailPage";
import { VerifyEmailMail } from "./pages/verify/VerifyEmailMail";
import { SignUpStepTwo } from "./pages/authentication/signup/SignUpStepTwo";
import { TermsAndConditions as LegalTerms } from "./pages/legal/TermsAndConditions";
import { PrivacyPolicy } from "./pages/legal/PrivacyPolicy";
import { ContactUs } from "./pages/legal/ContactUs";
import LoginVerifyTokenPage from "./pages/authentication/login/LoginVerifyTokenPage";
import { LinkedInCallbackPage } from "./pages/authentication/linkedin/LinkedInCallbackPage";
import SignUpStepThree from "./pages/authentication/signup/SignUpStepThree";
import SignUpStepFour from "./pages/authentication/signup/SignUpStepFour";
import SelectAdvisorsPage from "@/pages/onboarding/advisors/SelectAdvisorsPage";
import ConversationalGoalSetting from "@/pages/onboarding/goals/ConversationalGoalSetting";
import AdvisorAcceptancePage from "@/pages/onboarding/advisors/AdvisorAcceptancePage";
import { OAuthCallbackPage } from "@/pages/oauth/OAuthCallbackPage";
import { MainLayoutWithoutNavigation } from "./layouts/MainLayoutWithoutNavigation";
import { LeadVerifyPage } from "./pages/leads/LeadVerifyPage";
import { LeadMatchingPage } from "./pages/leads/LeadMatchingPage";
import { LeadEmailSentPage } from "./pages/leads/LeadEmailSentPage";
import { PabSessionProvider } from "@/context/PabSessionContext";
import { PublicProgramLandingPage } from "@/pages/programs/PublicProgramLandingPage";
// Landing page route is disabled (see the "/" route below); import kept for
// easy re-enable. Commented out because noUnusedLocals rejects unused imports.
// import { LandingPage } from "@/pages/landing-page";

// Lazy loaded routes
const HomePage = lazyWithRetry(() => import("@/pages/HomePage"));
const GettingStartedPage = lazyWithRetry(() =>
  import("@/pages/gettingStarted/GettingStartedPage").then((module) => ({
    default: module.GettingStartedPage,
  })),
);
const ExplorePage = lazyWithRetry(() =>
  import("@/pages/home/ExplorePage").then((module) => ({ default: module.ExplorePage })),
);
// const DashboardPage = lazyWithRetry(() => import("@/pages/DashboardPage"));
const GoalsRouter = lazyWithRetry(() => import("@/pages/goals/GoalsRouter"));
const ProfilePage = lazyWithRetry(() =>
  import("@/pages/profile/ProfilePage").then((module) => ({ default: module.ProfilePage })),
);
const PublicAdvisorProfilePage = lazyWithRetry(() =>
  import("@/pages/profile/PublicAdvisorProfilePage").then((module) => ({
    default: module.PublicAdvisorProfilePage,
  })),
);
const PublicAdvisorsDirectoryPage = lazyWithRetry(() =>
  import("@/pages/profile/PublicAdvisorsDirectoryPage").then((module) => ({
    default: module.PublicAdvisorsDirectoryPage,
  })),
);
const PabPage = lazyWithRetry(() => import("@/pages/pab/pabPage"));
const JourneyPortfolioPage = lazyWithRetry(() =>
  import("@/pages/journey/JourneyPortfolioPage").then((m) => ({ default: m.JourneyPortfolioPage })),
);
const ProgramsPage = lazyWithRetry(() =>
  import("@/pages/programs/ProgramsPage").then((m) => ({ default: m.ProgramsPage })),
);
const ProgramCheckoutPage = lazyWithRetry(() =>
  import("@/pages/programs/ProgramCheckoutPage").then((m) => ({
    default: m.ProgramCheckoutPage,
  })),
);
const ProgramDetailPage = lazyWithRetry(() =>
  import("@/pages/programs/ProgramDetailPage").then((m) => ({ default: m.ProgramDetailPage })),
);
const CreateProgramPage = lazyWithRetry(() =>
  import("@/pages/programs/CreateProgramPage").then((m) => ({ default: m.CreateProgramPage })),
);
const NewProgramTypePage = lazyWithRetry(() =>
  import("@/pages/programs/NewProgramTypePage").then((m) => ({ default: m.NewProgramTypePage })),
);
const PeerApplicationsPage = lazyWithRetry(() =>
  import("@/pages/peer-advisory/applications/PeerApplicationsPage").then((m) => ({
    default: m.PeerApplicationsPage,
  })),
);
const PeerApplyPage = lazyWithRetry(() =>
  import("@/pages/peer-advisory/apply/PeerApplyPage").then((m) => ({
    default: m.PeerApplyPage,
  })),
);
const PeerAdvisoryProgramViewPage = lazyWithRetry(() =>
  import("@/pages/peer-advisory/PeerAdvisoryProgramViewPage").then((m) => ({
    default: m.PeerAdvisoryProgramViewPage,
  })),
);
const PeerAdvisoryWizardPage = lazyWithRetry(() =>
  import("@/pages/peer-advisory/PeerAdvisoryWizardPage").then((m) => ({
    default: m.PeerAdvisoryWizardPage,
  })),
);
const OrganisationProgramWizardPage = lazyWithRetry(() =>
  import("@/pages/organisation-program/OrganisationProgramWizardPage").then((m) => ({
    default: m.OrganisationProgramWizardPage,
  })),
);
const OrganisationProgramPage = lazyWithRetry(() =>
  import("@/pages/organisation-program/OrganisationProgramPage").then((m) => ({
    default: m.OrganisationProgramPage,
  })),
);
const ProgramMemberDetailPage = lazyWithRetry(() =>
  import("@/pages/programs/ProgramMemberDetailPage").then((m) => ({
    default: m.ProgramMemberDetailPage,
  })),
);
const AchieverProgramDetailPage = lazyWithRetry(() =>
  import("@/pages/programs/AchieverProgramDetailPage").then((m) => ({
    default: m.AchieverProgramDetailPage,
  })),
);
const ProgramJourneyDetailPage = lazyWithRetry(() =>
  import("@/pages/programs/ProgramJourneyDetailPage").then((m) => ({
    default: m.ProgramJourneyDetailPage,
  })),
);
const ProgramWrapupPage = lazyWithRetry(() =>
  import("@/pages/programs/ProgramWrapupPage").then((m) => ({
    default: m.ProgramWrapupPage,
  })),
);
const ProgramReportPage = lazyWithRetry(() =>
  import("@/pages/programs/ProgramReportPage").then((m) => ({
    default: m.ProgramReportPage,
  })),
);
const ProgramWrapupArchivePage = lazyWithRetry(() =>
  import("@/pages/programs/ProgramWrapupArchivePage").then((m) => ({
    default: m.ProgramWrapupArchivePage,
  })),
);
const AdvisorPabDetailPage = lazyWithRetry(() =>
  import("@/pages/pab/advisor/AdvisorPabDetailPage").then((m) => ({
    default: m.AdvisorPabDetailPage,
  })),
);
const AchieverPabDetailPage = lazyWithRetry(() =>
  import("@/pages/pab/AchieverPabDetailPage").then((m) => ({
    default: m.AchieverPabDetailPage,
  })),
);
const ViewAdvisorProfilePage = lazyWithRetry(() =>
  import("@/pages/pab/ViewAdvisorProfilePage").then((m) => ({
    default: m.ViewAdvisorProfilePage,
  })),
);
const SessionsPage = lazyWithRetry(() => import("@/pages/sessions/SessionsPage"));
const AdminPanelPage = lazyWithRetry(() => import("@/pages/admin/AdminPanelPage"));
const MindSharePage = lazyWithRetry(() =>
  import("@/pages/network/MindSharePage").then((m) => ({ default: m.MindSharePage })),
);
const EventsPage = lazyWithRetry(() =>
  import("@/pages/network/EventsPage").then((m) => ({ default: m.EventsPage })),
);
const EventDetailPage = lazyWithRetry(() =>
  import("@/pages/network/EventDetailPage").then((m) => ({ default: m.EventDetailPage })),
);
const DirectoryPage = lazyWithRetry(() =>
  import("@/pages/network/DirectoryPage").then((m) => ({ default: m.DirectoryPage })),
);
const StarsPage = lazyWithRetry(() =>
  import("@/pages/stars/StarsPage").then((m) => ({ default: m.StarsPage })),
);
const StarDetailPage = lazyWithRetry(() =>
  import("@/pages/stars/StarDetailPage").then((m) => ({ default: m.StarDetailPage })),
);
const WrapupPage = lazyWithRetry(() =>
  import("@/pages/wrapup/WrapupPage").then((m) => ({ default: m.WrapupPage })),
);
const BuildPabWizardPage = lazyWithRetry(() =>
  import("@/pages/onboarding/build-pab/BuildPabWizardPage").then((m) => ({
    default: m.BuildPabWizardPage,
  })),
);
const ApplicationWizardPage = lazyWithRetry(() =>
  import("@/pages/onboarding/application/ApplicationWizardPage").then((m) => ({
    default: m.ApplicationWizardPage,
  })),
);
const WrapupArchivePage = lazyWithRetry(() =>
  import("@/pages/wrapup/WrapupArchivePage").then((m) => ({ default: m.WrapupArchivePage })),
);

/** Redirects /wrapup to the last saved step for this user, or step1 if none stored */
const WrapupRedirect = () => {
  const { user } = useAuth();
  const saved = user?.wrapupStep ?? 0;
  const valid = saved >= 1 && saved <= 7 ? saved : 1;
  return <Navigate to={`/wrapup/step${valid}`} replace />;
};

function App() {
  return (
    <ErrorBoundary>
      <Routes>
        {/* Auth routes */}
        {/* Login — full-screen layout, no AuthLayout wrapper */}
        <Route path="/login" element={<LoginPage />} />

        {/* The three sign-in landing pages are OUTSIDE AuthLayout, on purpose.
            Each is a redirect with a network call in the middle and shows only a
            spinner; AuthLayout would wrap that spinner in a bordered card with a
            logo and a footer, which reads as a screen the user has arrived at
            rather than one they are passing through. That framing is what the
            countdowns these pages used to run made worse. See AuthTransition. */}
        <Route path="/login-verify-token" element={<LoginVerifyTokenPage />} />
        <Route path="/email-verified" element={<VerifyEmailMail />} />

        <Route element={<AuthLayout />}>
          {/* Lead routes */}
          <Route path="/leads/email-sent" element={<LeadEmailSentPage />} />
          <Route path="/leads/verify" element={<LeadVerifyPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route path="/legal/terms" element={<LegalTerms />} />
          <Route path="/legal/privacy" element={<PrivacyPolicy />} />
          <Route path="/legal/contact" element={<ContactUs />} />
          <Route path="/advisor-accepted" element={<AdvisorAcceptancePage />} />
        </Route>
        {/* LinkedIn OAuth success — backend redirects here after processing */}
        <Route path="/auth/linkedin/success" element={<LinkedInCallbackPage />} />
        <Route
          path="/advisors"
          element={
            <Suspense
              fallback={
                <div className="flex h-full w-full items-center justify-center">
                  <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                </div>
              }
            >
              <PublicAdvisorsDirectoryPage />
            </Suspense>
          }
        />
        <Route
          path="/profile/:slug"
          element={
            <Suspense
              fallback={
                <div className="flex h-full w-full items-center justify-center">
                  <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                </div>
              }
            >
              <PublicAdvisorProfilePage />
            </Suspense>
          }
        />

        {/* Email sent — full-screen layout, no AuthLayout wrapper */}
        <Route path="/email-sent" element={<VerifyEmailPage />} />
        <Route path="/login-email-check" element={<LoginEmailCheckPage />} />

        {/* Public program landing page — no auth required */}
        <Route path="/join/:programId" element={<PublicProgramLandingPage />} />

        {/* Signup routes — full-screen layout, no AuthLayout wrapper */}
        <Route
          path="/signup"
          element={
            <OnboardingGuard>
              <SignUpPage />
            </OnboardingGuard>
          }
        />
        <Route
          path="/signup/2"
          element={
            <OnboardingGuard>
              <SignUpStepTwo />
            </OnboardingGuard>
          }
        />
        <Route
          path="/signup/3"
          element={
            <OnboardingGuard>
              <SignUpStepThree />
            </OnboardingGuard>
          }
        />
        {/* Experts only: S.T.A.R. Portfolio. Achievers finish at /signup/3. */}
        <Route
          path="/signup/4"
          element={
            <OnboardingGuard>
              <SignUpStepFour />
            </OnboardingGuard>
          }
        />

        {/* Protected routes */}
        <Route element={<MainLayout />}>
          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <PabSessionProvider>
                    {/* Sends a user with an unfinished payment back to finish
                        it. UX only — the server enforces entitlement on every
                        write regardless. */}
                    <PaymentGuard>
                      <HomePage />
                    </PaymentGuard>
                  </PabSessionProvider>
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/explore"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <ExplorePage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* A program's detail view, as a real shareable URL.
              Renders the SAME ExplorePage component -- it reads :externalId
              from the route and shows the detail body instead of the browse
              body -- so the in-app shell (sidebar/header) is preserved. This
              is deliberately distinct from the public /join/:programId page,
              which is the logged-out visitor version; an authenticated user
              opening /join/:id is redirected here so a shared link always
              lands in the right context for whoever opens it. */}
          <Route
            path="/explore/:externalId"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <ExplorePage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <DashboardPage />
                </Suspense>
              </ProtectedRoute>
            }
          /> */}
          <Route
            path="/goals"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <GoalsRouter />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/getting-started"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <GettingStartedPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <ProfilePage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/programs"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <ProgramsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* The program-type chooser. /programs/new used to render the Expert
              wizard directly; that flow now starts at /programs/new/expert. */}
          <Route
            path="/programs/new"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <NewProgramTypePage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* MUST precede /programs/new/:externalId, or "expert" is captured as
              a draft's externalId and the wizard fetches a program that can't
              exist. */}
          <Route
            path="/programs/new/expert"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <CreateProgramPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/programs/new/:externalId"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <CreateProgramPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* Peer Advisory application (achiever applying to join a peer group).
              INSIDE the app shell -- sidebar, header and all. The design is
              explicit about this: its header block is `position:sticky;top:64px`
              and its footer `left:248px`, i.e. pinned below the 64px app header
              and right of the 248px sidebar. Someone part-way through an
              application has not left the product, so they keep their nav.

              (Contrast the Expert Application and Build-a-PAB wizards, which ARE
              full-screen: those are onboarding flows run once, before the user
              has a shell worth keeping.)

              The step lives in ?step= rather than the path, the way the peer
              CREATION wizard does, because a resumed draft's step comes from the
              server (last_step) and is a query concern, not an address.

              This is the URL the PEER_APPLICATION_INFO_REQUESTED notification
              deep-links to -- do not rename it without updating that template. */}
          <Route
            path="/peer-advisory/:externalId/apply"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <PeerApplyPage />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Applications & Requests — the facilitator's review inbox.
              Both paths render the same page; the :externalId form just
              preselects one program, which is what the
              PEER_APPLICATION_RECEIVED notification deep-links to.

              The literal `applications` segment MUST stay declared before the
              `/peer-advisory/:externalId/...` siblings below (see the note on
              /programs/new/expert) or it would be matched as an externalId. */}
          <Route
            path="/peer-advisory/applications"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <PeerApplicationsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* Read-only view of a published or finished Peer program. The
              static /peer-advisory/new and /peer-advisory/applications routes
              outrank this dynamic one. */}
          <Route
            path="/peer-advisory/:externalId"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <PeerAdvisoryProgramViewPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/peer-advisory/:externalId/applications"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <PeerApplicationsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Peer Advisory wizard. Both paths render the same component: with no
              :externalId it starts a fresh draft, and step 1's save redirects to
              the /:externalId form. */}
          <Route
            path="/peer-advisory/new"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <PeerAdvisoryWizardPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/peer-advisory/new/:externalId"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <PeerAdvisoryWizardPage />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Organisation program wizard -- same two-path pattern as Peer
              Advisory. The literal "new" segment must stay above the
              /:externalId program page below. */}
          <Route
            path="/organisation-programs/new"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <OrganisationProgramWizardPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/organisation-programs/new/:externalId"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <OrganisationProgramWizardPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/organisation-programs/:externalId"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <OrganisationProgramPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/programs/:externalId/checkout"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <ProgramCheckoutPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/programs/:externalId"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <ProgramDetailPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/programs/:externalId/report"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <ProgramReportPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/programs/:externalId/member/:membershipId"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <ProgramMemberDetailPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/programs/:externalId/details"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <AchieverProgramDetailPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/programs/:externalId/journey"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <ProgramJourneyDetailPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/journey-portfolio"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <JourneyPortfolioPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pab"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <PabPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pab/advisor/:advisorId"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <ViewAdvisorProfilePage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pab/:sessionId"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <AdvisorPabDetailPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pab/:sessionId/details"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <AchieverPabDetailPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/sessions"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <SessionsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/mindshare"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex h-full w-full items-center justify-center">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <MindSharePage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* The achiever workspace's Feed: XO's Mindshare, read-only. */}
          <Route
            path="/feed"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex h-full w-full items-center justify-center">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <MindSharePage readOnly />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/directory"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex h-full w-full items-center justify-center">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <DirectoryPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/events"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex h-full w-full items-center justify-center">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <EventsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/events/:externalId"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex h-full w-full items-center justify-center">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <EventDetailPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* Blogs, playbooks and podcasts were folded into Mindshare posts.
              The old paths still resolve so bookmarks and links land on the
              feed instead of a 404. */}
          {["/blogs", "/playbooks", "/podcasts"].map((path) => (
            <Route key={path} path={path} element={<Navigate to="/feed" replace />} />
          ))}
          <Route
            path="/stars"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex h-full w-full items-center justify-center">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <StarsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/stars/:sessionId/:okrId/:starId"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex h-full w-full items-center justify-center">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <StarDetailPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
        </Route>
        {/* Admin panel. AdminGuard enforces `super_admin` in every
            environment -- unlike RoleGuard, which is open on non-prod. */}
        <Route path="/admin" element={<Navigate to="/admin/overview" replace />} />
        <Route
          path="/admin/:tab/:itemId?"
          element={
            <AdminGuard>
              <Suspense
                fallback={
                  <div className="flex h-full w-full items-center justify-center">
                    <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                  </div>
                }
              >
                <AdminPanelPage />
              </Suspense>
            </AdminGuard>
          }
        />
        {/* The standalone impersonation tool now lives in the panel's Users
            tab. Redirected rather than kept, so the weaker RoleGuard-protected
            door to impersonation does not stay open. */}
        <Route path="/account-access" element={<Navigate to="/admin/users" replace />} />
        <Route element={<MainLayoutWithoutNavigation />}>
          <Route
            path="/onboarding/goal-setting"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <ConversationalGoalSetting />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding/advisor-matching"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                      <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                    </div>
                  }
                >
                  <SelectAdvisorsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Lead matching route - full screen like onboarding */}
          <Route path="/leads/matching/:userId" element={<LeadMatchingPage />} />
        </Route>
        {/* OAuth callback - public, no layout */}
        <Route path="/oauth/callback" element={<OAuthCallbackPage />} />

        {/* Build Your PAB wizard — full-screen, no sidebar layout */}
        <Route
          path="/onboarding/build-pab/:stepNumber"
          element={
            <ProtectedRoute>
              <Suspense
                fallback={
                  <div className="flex min-h-screen w-full items-center justify-center bg-[#0c1220]">
                    <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                  </div>
                }
              >
                <BuildPabWizardPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Expert Application wizard (achiever applying to become an Expert) — full-screen, no sidebar layout */}
        <Route
          path="/application/:stepNumber"
          element={
            <ProtectedRoute>
              <Suspense
                fallback={
                  <div className="flex min-h-screen w-full items-center justify-center bg-[#0c1220]">
                    <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                  </div>
                }
              >
                <ApplicationWizardPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Wrapup flow — full-screen, no sidebar layout */}
        {/* /wrapup redirects to last saved step (or step1) */}
        <Route
          path="/wrapup"
          element={
            <ProtectedRoute>
              <WrapupRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/wrapup/:stepNumber"
          element={
            <ProtectedRoute>
              <Suspense
                fallback={
                  <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                    <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                  </div>
                }
              >
                <WrapupPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Wrapup flow — full-screen, no sidebar layout */}
        {/* /wrapup redirects to last saved step (or step1) */}
        <Route
          path="/wrapup"
          element={
            <ProtectedRoute>
              <WrapupRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/wrapup/:stepNumber"
          element={
            <ProtectedRoute>
              <Suspense
                fallback={
                  <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                    <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                  </div>
                }
              >
                <WrapupPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/wrapup/archive"
          element={
            <ProtectedRoute>
              <Suspense
                fallback={
                  <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                    <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                  </div>
                }
              >
                <WrapupArchivePage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Program wrapup flow — full-screen, no sidebar (same as PAB wrapup) */}
        <Route
          path="/programs/:externalId/wrapup/:stepNumber"
          element={
            <ProtectedRoute>
              <Suspense
                fallback={
                  <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                    <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                  </div>
                }
              >
                <ProgramWrapupPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/programs/:externalId/wrapup/archive"
          element={
            <ProtectedRoute>
              <Suspense
                fallback={
                  <div className="flex min-h-screen w-full items-center justify-center bg-theme-bg-page">
                    <Loader2 className="h-20 w-20 animate-spin text-primary-500" />
                  </div>
                }
              >
                <ProgramWrapupArchivePage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Landing page — temporarily disabled. The marketing landing page
            ("Accelerate Your Future Journey with Your Dream Board") is kept in
            the codebase at @/pages/landing-page; re-enable by uncommenting the
            route below and removing the redirect that replaces it. */}
        {/* <Route path="/" element={<LandingPage />} /> */}

        {/* Root now goes straight to the login page. */}
        <Route path="/" element={<Navigate to="/login" replace />} />

        {/* Catch all - 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </ErrorBoundary>
  );
}

export default App;
